import React, { useState, useEffect, memo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card } from './ui/card';
import { Home, Phone, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Optimized image component for lodges
const LodgeImage = memo(({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  
  return (
    <div className="relative h-64 overflow-hidden">
      <div 
        className={`absolute inset-0 bg-gradient-to-br from-emerald-900 to-stone-800 transition-opacity duration-500 ${
          loaded ? 'opacity-0' : 'opacity-100'
        }`}
      />
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/20 to-transparent" />
    </div>
  );
});

LodgeImage.displayName = 'LodgeImage';

const Lodges = memo(() => {
  const { t, language } = useLanguage();
  const [lodges, setLodges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLodges();
  }, []);

  const fetchLodges = async () => {
    try {
      const response = await axios.get(`${API}/lodges`);
      if (response.data && response.data.length > 0) {
        setLodges(response.data);
      } else {
        // Seed if empty
        await axios.post(`${API}/seed-lodges`);
        const retryResponse = await axios.get(`${API}/lodges`);
        setLodges(retryResponse.data);
      }
    } catch (error) {
      console.error('Error fetching lodges:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lodges" className="py-24 bg-stone-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-emerald-400 mb-4">
            <Home className="w-5 h-5" />
            <span className="text-sm font-semibold tracking-wider uppercase">
              {t.lodges.title}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white">
            {t.lodges.title}
          </h2>
        </div>

        {/* Lodges Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            {[1, 2].map((i) => (
              <Card key={i} className="bg-stone-900/50 border-stone-800 overflow-hidden">
                <div className="h-64 bg-stone-800 animate-pulse" />
                <div className="p-6 space-y-4">
                  <div className="h-6 bg-stone-800 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-stone-800 rounded animate-pulse" />
                  <div className="h-4 bg-stone-800 rounded animate-pulse w-1/2" />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {lodges.map((lodge) => (
              <Card
                key={lodge.id}
                className="bg-stone-900/50 border-stone-800 overflow-hidden hover:border-emerald-600/30 transition-all duration-300 group relative"
                data-testid={`lodge-card-${lodge.id}`}
              >
                {/* Image */}
                <LodgeImage 
                  src={lodge.image} 
                  alt={language === 'sl' ? lodge.name_sl : lodge.name_en}
                />
                <div className="absolute top-[220px] left-4 z-10">
                  <h3 className="text-2xl font-bold text-white drop-shadow-lg">
                    {language === 'sl' ? lodge.name_sl : lodge.name_en}
                  </h3>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-stone-400 mb-6 leading-relaxed">
                    {language === 'sl' ? lodge.description_sl : lodge.description_en}
                  </p>

                  {/* Manager Info */}
                  <div className="flex items-center justify-between pt-4 border-t border-stone-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                        <User className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm text-stone-500">
                          {language === 'sl' ? 'Upravnik' : 'Manager'}
                        </p>
                        <p className="text-white font-medium">{lodge.manager}</p>
                      </div>
                    </div>
                    <a
                      href={`tel:${lodge.phone.replace(/\s/g, '')}`}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600/20 rounded-lg text-emerald-400 hover:bg-emerald-600/30 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{lodge.phone}</span>
                    </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
});

Lodges.displayName = 'Lodges';

export default Lodges;
